"use client";

import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import Calendar from "../../components/common/Calendar";
import Filters from "../../components/common/Filters";
import Scan from "../../assets/icons/arrow-right-left.svg?react";
import Up from "../../assets/icons/chevron-up.svg?react";
import Down from "../../assets/icons/chevron-down.svg?react";
import ConverterPop from "../../components/Popup/ConverterPop";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-hot-toast";

// flowbite icon caret down
import { ArrowBigDown, RefreshCw, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { gamesApi } from "../../api/games";
import { getGameImage } from "../../utils/getGameImage";
import getGameType from "../../utils/games";

export default function CommissionBar({
  onFilterChange,
  commissionTypes,
  selectedType,
  userData,
  onWalletUpdate,
  gameTotals,
  isLoadingTotals
}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isConverterOpen, setIsConverterOpen] = useState(false);
  const [isGameDropdownOpen, setIsGameDropdownOpen] = useState({});
  const { tokens } = useAuth();
  const [gameSettings, setGameSettings] = useState([]);
  
  // Get commission from either meta.commissions or commissions property
  const getCommissionAmount = () => {
      // Handle null userData case
      if (!userData) return "0.00";
      
      // Use optional chaining to safely access nested properties
      const metaCommissions = userData?.meta?.commissions;
      const directCommissions = userData?.commissions;
      
      if (metaCommissions !== undefined && metaCommissions !== null) {
        return Number(metaCommissions).toFixed(2);
      } else if (directCommissions !== undefined && directCommissions !== null) {
        return Number(directCommissions).toFixed(2);
      }
      
      return "0.00";
  };
  
  const handleConversionSuccess = (updatedWallet) => {
    if (onWalletUpdate) {
      onWalletUpdate(updatedWallet);
    }
  };

  useEffect(() => {
    const fetchGameSettings = async () => {
      try {
        const response = await gamesApi.getAllGames(tokens?.access?.token);
        if (response.data) {
          setGameSettings(response.data);
        }
      } catch (error) {
        console.error("Error fetching game settings:", error);
      }
    };
    
    fetchGameSettings();
  }, [tokens?.access?.token]);
  
  const getGameSettingImage = (gameSettingsId) => {
    const gameSetting = gameSettings.find(game => game.id === gameSettingsId);
    if (!gameSetting?.otherSettings?.image) return null;
    
    const gameType = getGameType(gameSetting.otherSettings.image);
    return getGameImage(gameType);
  };

  const handleFilterClick = (gameSettingsId) => {
    if (onFilterChange) {
      onFilterChange(gameSettingsId === 'all' ? 'all' : gameSettingsId);
    }
  };
  
  const handleGameFilterClick = (gameId) => {
    if (onFilterChange) {
      // Using 'game:' prefix to distinguish from settings ID filtering
      onFilterChange(`game:${gameId}`);
    }
  };
  
  const toggleGameDropdown = (settingsId) => {
    setIsGameDropdownOpen(prev => ({
      ...prev,
      [settingsId]: !prev[settingsId]
    }));
  };
  
  return (
    <>
      <div className="space-y-4 mb-2">
        <div className="flex items-center gap-2">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            {isFilterOpen ? <Up /> : <Down />}
          </div>

          {/* Header */}
          <div className="flex-1 text-center">
            <div className="text-[10px] text-[#65758B] leading-4 font-normal">
              Overall Total Commissions
            </div>
            <div className="text-3xl text-[#0E64D1] font-semibold">
              ₱{getCommissionAmount()}
            </div>
          </div>

          {/* Scan Button */}
          <button 
            variant="outline" 
            size="icon"
            onClick={() => setIsConverterOpen(true)}
          >
            <Scan width={30} height={30} className="rounded-lg" />
          </button>
        </div>

        {/* Dropdown */}
        <div
          className={`transition-all duration-300 ${
            isFilterOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden`}
        >
          {isLoadingTotals ? (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-gray-600">Loading complete data...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 mt-2">
              {/* All Games Filter */}
              <div 
                className={`flex items-center gap-2 border rounded-md cursor-pointer ${
                  selectedType === 'all' ? 'border-blue-500' : 'border-gray-200'
                }`}
                onClick={() => handleFilterClick('all')}
              >
                <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center">
                  <RefreshCw className="text-white" size={18} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] text-gray-500 font-normal">
                    Overall
                  </div>
                  <div className="text-base font-semibold text-[#0E64D1]">
                    ₱{getCommissionAmount()}
                  </div>
                </div>
              </div>
              
              {/* Game Settings Filters */}
              {gameTotals && gameTotals.gameSettingsTotals && Object.entries(gameTotals.gameSettingsTotals).map(([settingsId, data]) => {
                const gameImage = getGameSettingImage(Number(settingsId));
                const isGameDropdownVisible = isGameDropdownOpen[settingsId] || false;
                
                // Use different background colors based on settings ID
                const getCardColor = (id) => {
                  const colorMap = {
                    '16': { bg: '#0078FF', text: '#fff' }, // 2D
                    '1': { bg: '#7E01FF', text: '#fff' },  // 3D
                    '15': { bg: '#01ABFF', text: '#fff' }, // 4D
                    '13': { bg: '#FF5100', text: '#fff' }, // STL
                    '12': { bg: '#FFCB00', text: '#000' }, // LOTTO
                    '30': { bg: '#00D1FF', text: '#fff' }  // DEFAULT
                  };
                  
                  return colorMap[id] || { bg: '#65758B', text: '#fff' }; // Fallback color
                };
                
                const colors = getCardColor(settingsId);
                
                // Find all games for this setting
                const gamesForThisSetting = {};
                if (gameTotals.gameTotals) {
                  Object.entries(gameTotals.gameTotals).forEach(([gameId, gameData]) => {
                    if (gameData.settingsId && gameData.settingsId.toString() === settingsId.toString()) {
                      gamesForThisSetting[gameId] = gameData;
                    }
                  });
                }
                
                const hasIndividualGames = Object.keys(gamesForThisSetting).length > 0;
                
                // Get game type label based on settings ID
                const getGameTypeLabel = (id) => {
                  const labelMap = {
                    '16': '2D',
                    '1': '3D',
                    '15': '4D',
                    '13': 'STL',
                    '12': 'LOTTO',
                    '30': 'Other'
                  };
                  
                  return labelMap[id] || "Game";
                };
                
                const gameTypeLabel = getGameTypeLabel(settingsId);
                
                return (
                  <div key={settingsId} className="mb-1">
                    <div 
                      className={`flex items-center gap-2 border rounded-md cursor-pointer ${
                        selectedType === settingsId ? 'border-blue-500' : 'border-gray-200'
                      }`}
                      onClick={() => {
                        if (hasIndividualGames) {
                          toggleGameDropdown(settingsId);
                        } else {
                          handleFilterClick(settingsId);
                        }
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-md overflow-hidden flex items-center justify-center"
                        style={{ backgroundColor: colors.bg }}
                      >
                        {gameImage ? (
                          <img src={gameImage} alt="Game" className="w-8 h-8 object-contain" />
                        ) : (
                          <span className="text-sm font-bold" style={{ color: colors.text }}>
                            {gameTypeLabel}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] text-gray-500 font-normal">
                          {gameTypeLabel}
                        </div>
                        <div className="text-base font-semibold text-[#0E64D1]">
                          ₱{data.amount.toFixed(2)}
                        </div>
                      </div>
                      {hasIndividualGames && (
                        <div className="pr-1">
                          {isGameDropdownVisible ? 
                            <ChevronUp size={14} className="text-gray-500" /> : 
                            <ChevronDown size={14} className="text-gray-500" />
                          }
                        </div>
                      )}
                    </div>
                    
                    {/* Individual Games Dropdown */}
                    {hasIndividualGames && isGameDropdownVisible && (
                      <div className="ml-4 mt-1 border-l px-2 border-gray-200 pl-1">
                        {Object.entries(gamesForThisSetting).map(([gameId, gameData]) => (
                          <div 
                            key={gameId}
                            className={`flex items-center gap-2 my-1 border rounded-md cursor-pointer ${
                              selectedType === `game:${gameId}` ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                            }`}
                            onClick={() => handleGameFilterClick(gameId)}
                          >
                            {/* <div 
                              className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center"
                              style={{ backgroundColor: colors.bg }}
                            >
                              <span className="text-xs font-bold" style={{ color: colors.text }}>
                                {gameTypeLabel}
                              </span>
                            </div> */}
                            <div className="flex-1">
                              <div className="text-[10px] text-gray-500 font-normal truncate max-w-[120px]">
                                {gameData.name}
                              </div>
                              <div className="text-sm font-semibold text-[#0E64D1]">
                                ₱{gameData.amount.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      <ConverterPop 
        isOpen={isConverterOpen}
        onClose={() => setIsConverterOpen(false)}
        token={tokens?.access?.token}
        onConversionSuccess={handleConversionSuccess}
        userCommission={getCommissionAmount()}
      />
    </>
  );
}
